import React from 'react';
import Bugsnag, { NotifiableError } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

const BUGSNAG_FRONTEND_API_KEY =
  process.env.NEXT_PUBLIC_BUGSNAG_FRONTEND_API_KEY!;

const isServer = typeof window === 'undefined';

if (!isServer) {
  if (!Bugsnag.isStarted()) {
    Bugsnag.start({
      apiKey: BUGSNAG_FRONTEND_API_KEY,
      appVersion: process.env.BUILD_TIME_APP_VERSION!,
      plugins: [new BugsnagPluginReact()],
      releaseStage: process.env.NEXT_PUBLIC_STAGE!,
    });
    BugsnagPerformance.start(BUGSNAG_FRONTEND_API_KEY);
  }
}

export const BugsnagContext = isServer
  ? null
  : Bugsnag.getPlugin('react')?.createErrorBoundary(React);

//  Treat silly playback errors as benign: https://app.clickup.com/t/9003040700/LING-7746
export const isBenignPlayInterruption = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const ex = err as DOMException;
  const msg = (ex.message ?? '').toString();

  return (
    ex.name === 'AbortError' ||
    msg.includes('The play() request was interrupted') ||
    ex.name === 'NotAllowedError' ||
    msg.includes('NotAllowedError') ||
    msg.includes('play() failed because')
  );
};

export const isSafeToIgnoreFirebaseAuthError = (err: unknown): boolean => {
  if (typeof (err as any)?.code !== 'string') {
    return false;
  }

  const ignoredCodes = new Set([
    'auth/user-not-found',
    'auth/wrong-password',
    'auth/user-token-expired',
    'auth/cancelled-popup-request',
    'auth/credential-already-in-use',
    'auth/user-cancelled',
    'auth/account-exists-with-different-credential',
    'auth/email-already-in-use',
    'auth/popup-blocked',
  ]);

  const code = (err as any).code;

  return ignoredCodes.has(code);
};

export const setUser = (id: string, email?: string, name?: string) => {
  Bugsnag.setUser(id, email, name);
};

export const leaveBreadcrumb = (
  message: string,
  metadata: Record<string, any>,
) => {
  Bugsnag.leaveBreadcrumb(message, metadata);
};

export const track = (err: NotifiableError | unknown) => {
  // Ignore benign playback errors
  if (isBenignPlayInterruption(err)) return; 
  // Ignore handled or insignificant Firebase auth errors
  if (isSafeToIgnoreFirebaseAuthError(err)) return; 

  // Convert error to a format Bugsnag can handle
  const notifiableError = makeNotifiable(err);

  Bugsnag.notify(notifiableError, () => {
    console.error('Error reporting to Bugsnag on client', err);
  });
};

/**
 * Converts any error type to a Bugsnag-compatible NotifiableError
 */
export const makeNotifiable = (err: unknown): NotifiableError => {
  // If it's already an Error object, return it
  if (err instanceof Error) return err;

  // Handle null and undefined separately with specific messages
  if (err === null) return new Error('Null error');
  if (err === undefined) return new Error('Undefined error');

  // If it's a string, wrap it in an Error
  if (typeof err === 'string') return new Error(err);

  // If it's an object, try to extract useful information
  if (typeof err === 'object') {
    try {
      // Try to create an error with the object's string representation
      const objStr = JSON.stringify(err);
      // Create an error with the stringified object information
      return new Error(`Object error: ${objStr}`);
    } catch {
      // If JSON.stringify fails (circular references, etc.)
      return new Error(
        `Unserializable object error: ${Object.prototype.toString.call(err)}`,
      );
    }
  }

  // For other types (number, boolean, symbol, etc.)
  return new Error(`Non-object error: ${String(err)}`);
};
